import { CommonModule } from '@angular/common';

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit
} from '@angular/core';

import { FormsModule } from '@angular/forms';

import {
  CommunityPost,
  DeleteRequest
} from '../../../../models/app.models';

import { CommunityPostService } from '../../../../services/community-post.service';
import { MediaService } from '../../../../services/media.service';
import { ToastService } from '../../../../services/toast.service';

import { environment } from '../../../../../environments/environment';


@Component({
  selector: 'app-admin-community',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule
  ],
  templateUrl: './admin-community.component.html',
  styleUrls: [
    './admin-community.component.css',
    '../../admin-section.shared.css'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminCommunityComponent
  implements OnInit {

  readonly requiredFieldsMessage =
    'Complete all required fields.';

  communityPosts: CommunityPost[] = [];

  communityPostsLoading = true;

  selectedPostImage: File | null = null;

  postEditorOpen = false;

  postDraft: CommunityPost =
    this.emptyPost();

  deleteRequest: DeleteRequest | null = null;


  constructor(
    private readonly communityPostService:
      CommunityPostService,
    private readonly mediaService:
      MediaService,
    private readonly toastService:
      ToastService,
    private readonly changeDetector:
      ChangeDetectorRef
  ) {}


  ngOnInit(): void {
    this.loadCommunityPosts();
  }


  // ============================================================
  // POST LISTS
  // ============================================================

  get livePosts(): CommunityPost[] {

    const today =
      this.getLocalDateString();

    return [...this.communityPosts]
      .filter(
        post =>
          post.date <= today
      )
      .sort(
        (a, b) =>
          b.date.localeCompare(a.date)
      );
  }


  get scheduledPosts(): CommunityPost[] {

    const today =
      this.getLocalDateString();

    return [...this.communityPosts]
      .filter(
        post =>
          post.date > today
      )
      .sort(
        (a, b) =>
          a.date.localeCompare(b.date)
      );
  }


  // ============================================================
  // LOAD POSTS
  // ============================================================

  loadCommunityPosts(): void {

    this.communityPostsLoading = true;

    this.communityPostService
      .getAdminPosts()
      .subscribe({

        next: posts => {

          this.communityPosts = posts;

          this.communityPostsLoading = false;

          this.changeDetector.markForCheck();
        },

        error: error => {

          console.error(
            'Unable to load community posts:',
            error
          );

          this.communityPostsLoading = false;

          this.toastService.error(
            'Unable to load community posts.'
          );

          this.changeDetector.markForCheck();
        }

      });
  }


  // ============================================================
  // EDITOR
  // ============================================================

  openPostEditor(
    post?: CommunityPost
  ): void {

    this.postDraft = post
      ? { ...post }
      : this.emptyPost();

    this.selectedPostImage = null;

    this.postEditorOpen = true;
  }


  closePostEditor(): void {

    this.selectedPostImage = null;

    this.postDraft =
      this.emptyPost();

    this.postEditorOpen = false;
  }


  // ============================================================
  // SAVE POST
  // ============================================================

  savePost(): void {

    if (
      !this.postDraft.title.trim() ||
      !this.postDraft.date ||
      !this.postDraft.description.trim()
    ) {

      this.toastService.error(
        this.requiredFieldsMessage
      );

      return;
    }

    if (this.selectedPostImage) {

      this.mediaService
        .uploadImage(
          this.selectedPostImage
        )
        .subscribe({

          next: media => {

            this.postDraft.imageId =
              media.id;

            this.persistPost();
          },

          error: error => {

            console.error(
              'Unable to upload community post image:',
              error
            );

            this.toastService.error(
              'Unable to upload image.'
            );

            this.changeDetector.markForCheck();
          }

        });

      return;
    }

    this.persistPost();
  }


  // ============================================================
  // IMAGE
  // ============================================================

  onImageSelected(
    event: Event,
    _target: 'post' | 'staff' = 'post'
  ): void {

    const input =
      event.target as HTMLInputElement;

    const file =
      input.files?.[0];

    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {

      this.toastService.error(
        'Please select a valid image.'
      );

      input.value = '';

      return;
    }

    this.selectedPostImage = file;

    const reader =
      new FileReader();

    reader.onload = () => {

      this.postDraft.imageUrl =
        String(reader.result ?? '');

      this.changeDetector.markForCheck();
    };

    reader.onerror = () => {

      this.toastService.error(
        'Unable to process the selected image.'
      );

      input.value = '';
    };

    reader.readAsDataURL(file);
  }


  removeDraftImage(
    _target: 'post' | 'staff' = 'post'
  ): void {

    this.postDraft.imageUrl = '';
    this.postDraft.imageId = null;

    this.selectedPostImage = null;

    this.changeDetector.markForCheck();
  }


  // ============================================================
  // DELETE
  // ============================================================

  requestDelete(
    kind: DeleteRequest['kind'],
    id: number,
    label: string
  ): void {

    this.deleteRequest = {
      kind,
      id,
      label
    };
  }


  confirmDelete(): void {

    if (!this.deleteRequest) {
      return;
    }

    this.deletePost(
      this.deleteRequest.id
    );
  }


  cancelDelete(): void {
    this.deleteRequest = null;
  }


  // ============================================================
  // TRACKING
  // ============================================================

  trackById(
    _: number,
    item: { id: number }
  ): number {

    return item.id;
  }


  // ============================================================
  // PERSIST POST
  // ============================================================

  private persistPost(): void {

    if (this.postDraft.id) {

      this.communityPostService
        .updatePost(
          this.postDraft
        )
        .subscribe({

          next: () => {

            const savedPost = {
              ...this.postDraft
            };

            if (savedPost.imageId) {

              savedPost.imageUrl =
                `${environment.apiUrl}/media/${savedPost.imageId}`;
            }

            this.communityPosts =
              this.communityPosts.map(
                post =>
                  post.id === savedPost.id
                    ? savedPost
                    : post
              );

            this.selectedPostImage = null;

            this.postDraft =
              this.emptyPost();

            this.postEditorOpen = false;

            this.toastService.success(
              'Community post saved.'
            );

            this.changeDetector.markForCheck();
          },

          error: error => {

            console.error(
              'Unable to update community post:',
              error
            );

            this.toastService.error(
              'Unable to save community post.'
            );

            this.changeDetector.markForCheck();
          }

        });

      return;
    }

    this.communityPostService
      .createPost(
        this.postDraft
      )
      .subscribe({

        next: savedPost => {

          this.communityPosts = [
            ...this.communityPosts,
            savedPost
          ];

          this.selectedPostImage = null;

          this.postDraft =
            this.emptyPost();

          this.postEditorOpen = false;

          this.toastService.success(
            'Community post saved.'
          );

          this.changeDetector.markForCheck();
        },

        error: error => {

          console.error(
            'Unable to create community post:',
            error
          );

          this.toastService.error(
            'Unable to save community post.'
          );

          this.changeDetector.markForCheck();
        }

      });
  }


  // ============================================================
  // DELETE POST
  // ============================================================

  private deletePost(
    id: number
  ): void {

    this.communityPostService
      .deletePost(id)
      .subscribe({

        next: () => {

          this.communityPosts =
            this.communityPosts.filter(
              post =>
                post.id !== id
            );

          this.deleteRequest = null;

          this.toastService.success(
            'Community post deleted.'
          );

          this.changeDetector.markForCheck();
        },

        error: error => {

          console.error(
            'Unable to delete community post:',
            error
          );

          this.toastService.error(
            'Unable to delete community post.'
          );

          this.changeDetector.markForCheck();
        }

      });
  }


  // ============================================================
  // HELPERS
  // ============================================================

  private getLocalDateString(): string {

    const now =
      new Date();

    const year =
      now.getFullYear();

    const month =
      String(
        now.getMonth() + 1
      )
        .padStart(
          2,
          '0'
        );

    const day =
      String(
        now.getDate()
      )
        .padStart(
          2,
          '0'
        );

    return `${year}-${month}-${day}`;
  }


  private emptyPost(): CommunityPost {

    return {
      id: 0,
      title: '',
      date:
        this.getLocalDateString(),
      description: '',
      link: '',
      imageId: null,
      imageUrl: ''
    };
  }

}